/**
 * Tip movement markers ("chevrons") for Lexi's three tip sets.
 *
 * A race has up to three tip sets, oldest to newest:
 *   Early Mail -> Race Day tips -> Late Mail
 * The LATEST set is the newest set the viewer can see (it is the list at the top).
 *
 * Rules:
 *  1. Latest set: each horse's marker shows how it moved SINCE the set before it.
 *  2. Older sets: each horse's marker shows where that horse is NOW (vs the latest set),
 *     so a horse in Race Day tips and Late Mail gets the same marker in both lists.
 *  3. Only the shown tips count (`shownCount`): with 2 tips shown, a horse that was
 *     tip 3 and is now tip 1 is "new".
 *  4. A scratched horse is "scratched" in every list (never "dropped" or "down").
 *  5. Fewer than two sets -> no markers at all.
 *
 * Callers pass ONLY the sets the viewer is allowed to see, so a locked set can never
 * leak through a marker.
 */
import { parseHorseFormattedName } from "./parse-horse-name";

export const TIP_SETS = Object.freeze({
  EARLY_MAIL: "earlyMail",
  RACE_DAY: "raceDay",
  LATE_MAIL: "lateMail",
});

export const TIP_SET_LABELS = Object.freeze({
  [TIP_SETS.EARLY_MAIL]: "Early Mail",
  [TIP_SETS.RACE_DAY]: "Race Day tips",
  [TIP_SETS.LATE_MAIL]: "Late Mail",
});

export const MOVEMENT = Object.freeze({
  UP: "up",
  DOWN: "down",
  NEW: "new",
  DROPPED: "dropped",
  SAME: "same",
  SCRATCHED: "scratched",
});

/** Owner switch: the one-line marker key under the tips (only shown when markers are). */
export const SHOW_TIP_MARKER_KEY = true;

/** "since" = latest set vs the set before it; "now" = older set vs the latest set. */
export const RELATION = Object.freeze({ SINCE: "since", NOW: "now" });

const asIdString = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s && s !== "N/A" ? s : null;
};

// "St. Patrick" / "St  Patrick" / "ST PATRICK" -> "st patrick"; "O'Reilly" -> "oreilly"
const normalizeName = (value) =>
  `${value || ""}`
    .toLowerCase()
    .replace(/['\u2019.]/g, "")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeNumber = (value) => {
  const s = asIdString(value);
  if (!s) return null;
  const m = s.match(/^0*(\d+)/);
  return m ? m[1] || "0" : null;
};

/**
 * Identity of one tip, whatever shape it comes in: raw backend tips
 * (`formattedName: "3. Overcast"`, `selectionId`, `horseId`) or rows already
 * transformed for display (`number`, `name`, ...).
 */
export const getRunnerIdentity = (tip) => {
  if (!tip || typeof tip !== "object") {
    return { selectionId: null, horseId: null, name: "", number: null };
  }
  const parsed = parseHorseFormattedName(tip);
  return {
    selectionId: asIdString(tip.selectionId ?? tip.selectionID),
    horseId: asIdString(tip.horseId),
    name: normalizeName(parsed.name),
    number: normalizeNumber(parsed.number),
  };
};

/**
 * True when two tips are the same runner. Strongest shared id wins:
 * Betfair selectionId, then Punting Form horseId, then name, then saddlecloth number.
 */
export const isSameRunner = (a, b) => {
  const x = a?.__identity || getRunnerIdentity(a);
  const y = b?.__identity || getRunnerIdentity(b);
  if (x.selectionId && y.selectionId) return x.selectionId === y.selectionId;
  if (x.horseId && y.horseId) return x.horseId === y.horseId;
  if (x.name && y.name) return x.name === y.name;
  if (x.number && y.number) return x.number === y.number;
  return false;
};

// Keeps indexes aligned with the rendered rows: a broken entry becomes {} (never matches).
const toList = (tips) =>
  Array.isArray(tips)
    ? tips.map((t) => (t && typeof t === "object" ? t : {}))
    : [];

/** "HH:mm", "HH:mm:ss" or an ISO date-time -> minutes after midnight, else null. */
export const tipTimeToMinutes = (value) => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.valueOf())
      ? null
      : value.getHours() * 60 + value.getMinutes();
  }
  const raw = String(value).trim();
  const hhmm = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hhmm) return parseInt(hhmm[1], 10) * 60 + parseInt(hhmm[2], 10);
  if (/\d{4}-\d{2}-\d{2}[T\s]\d{1,2}:\d{2}/.test(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.valueOf())) return d.getHours() * 60 + d.getMinutes();
  }
  return null;
};

/** Late Mail exists only when the latest tips were updated after the Race Day tips. */
export const isLateMailUpdate = (latestTipUpdatedAt, raceDayTipUpdatedAt) => {
  const latest = tipTimeToMinutes(latestTipUpdatedAt);
  const raceDay = tipTimeToMinutes(raceDayTipUpdatedAt);
  if (latest === null || raceDay === null) return false;
  return latest > raceDay;
};

const tipsOf = (raceData) =>
  Array.isArray(raceData) ? toList(raceData) : toList(raceData?.firstFourData);

/**
 * Ordered (oldest -> newest) list of the tip sets that exist for a race.
 *
 * @param {Object} sources
 * @param {Array} [sources.earlyMail] - Early Mail tips (only when the viewer may see them)
 * @param {Object|Array} [sources.raceDay] - Race Day tips race data ({ firstFourData, tipUpdatedAt })
 * @param {Object|Array} [sources.latest] - latest tips race data (`predictions`): Late Mail when it was
 *   updated after the Race Day tips, otherwise the same Race Day tips
 * @returns {Array<{ id: string, tips: Array }>}
 */
export const buildTipSets = ({ earlyMail, raceDay, latest } = {}) => {
  const sets = [];
  const earlyMailTips = toList(earlyMail);
  const raceDayTips = tipsOf(raceDay);
  const latestTips = tipsOf(latest);

  if (earlyMailTips.length) {
    sets.push({ id: TIP_SETS.EARLY_MAIL, tips: earlyMailTips });
  }
  if (raceDayTips.length) {
    sets.push({ id: TIP_SETS.RACE_DAY, tips: raceDayTips });
    if (
      latestTips.length &&
      isLateMailUpdate(latest?.tipUpdatedAt, raceDay?.tipUpdatedAt)
    ) {
      sets.push({ id: TIP_SETS.LATE_MAIL, tips: latestTips });
    }
  } else if (latestTips.length) {
    // No separate Race Day snapshot: the latest tips are the Race Day tips.
    sets.push({ id: TIP_SETS.RACE_DAY, tips: latestTips });
  }
  return sets;
};

const scratchedIdSet = (scratchings) => {
  const ids = new Set();
  (Array.isArray(scratchings) ? scratchings : []).forEach((s) => {
    const id =
      s && typeof s === "object" ? asIdString(s.selectionId) : asIdString(s);
    if (id) ids.add(id);
  });
  return ids;
};

const rankWithin = (tips, tip, count) => {
  const idx = tips.findIndex((other, i) => i < count && isSameRunner(tip, other));
  return idx === -1 ? null : idx + 1;
};

const statusFor = (fromRank, toRank, missingStatus) => {
  if (fromRank === null || toRank === null) return missingStatus;
  if (fromRank === toRank) return MOVEMENT.SAME;
  return toRank < fromRank ? MOVEMENT.UP : MOVEMENT.DOWN;
};

/**
 * Marker for every shown tip of every set.
 *
 * @param {Array<{ id: string, tips: Array }>} sets - oldest -> newest, ONLY sets the viewer can see
 * @param {Object} [options]
 * @param {number} [options.shownCount=4] - tips shown per set (2 or 4)
 * @param {Array} [options.scratchings] - scratched runners (Betfair selectionIds or { selectionId })
 * @returns {Object|null} null when there is nothing to compare (fewer than two sets); otherwise
 *   `{ [setId]: Array<{ status, relation, comparedWith, fromRank, toRank } | null> }`, each array
 *   aligned with that set's `tips` (null past `shownCount`).
 */
export const getTipMovements = (sets, { shownCount = 4, scratchings } = {}) => {
  const usable = (Array.isArray(sets) ? sets : [])
    .map((set) => ({ id: set?.id, tips: toList(set?.tips) }))
    .filter((set) => set.id && set.tips.length > 0);
  if (usable.length < 2) return null;

  const count =
    Number.isFinite(shownCount) && shownCount > 0 ? Math.floor(shownCount) : 4;
  // Cache identities once (tips are not mutated: wrap them).
  const wrapped = usable.map((set) => ({
    id: set.id,
    tips: set.tips.map((tip) => ({ tip, __identity: getRunnerIdentity(tip) })),
  }));

  const scratched = scratchedIdSet(scratchings);
  const allTips = wrapped.flatMap((set) => set.tips);
  const isScratched = (w) => {
    if (scratched.size === 0) return false;
    if (w.__identity.selectionId) return scratched.has(w.__identity.selectionId);
    // Early Mail tips have no selectionId: borrow it from the same runner in another set.
    const twin = allTips.find(
      (other) => other.__identity.selectionId && isSameRunner(w, other),
    );
    return twin ? scratched.has(twin.__identity.selectionId) : false;
  };

  const latest = wrapped[wrapped.length - 1];
  const previous = wrapped[wrapped.length - 2];
  const result = {};

  wrapped.forEach((set) => {
    const isLatest = set === latest;
    result[set.id] = set.tips.map((w, index) => {
      if (index >= count) return null;
      const position = index + 1;
      const base = isLatest
        ? {
            relation: RELATION.SINCE,
            comparedWith: previous.id,
            fromRank: rankWithin(previous.tips, w, count),
            toRank: position,
          }
        : {
            relation: RELATION.NOW,
            comparedWith: latest.id,
            fromRank: position,
            toRank: rankWithin(latest.tips, w, count),
          };
      const status = isScratched(w)
        ? MOVEMENT.SCRATCHED
        : statusFor(
            base.fromRank,
            base.toRank,
            isLatest ? MOVEMENT.NEW : MOVEMENT.DROPPED,
          );
      return { status, ...base };
    });
  });

  return result;
};

/** Short customer-facing text for a marker (title + screen readers). */
export const describeTipMovement = (movement) => {
  if (!movement?.status) return "";
  const label = TIP_SET_LABELS[movement.comparedWith] || "earlier tips";
  if (movement.status === MOVEMENT.SCRATCHED) return "Scratched";
  if (movement.relation === RELATION.SINCE) {
    switch (movement.status) {
      case MOVEMENT.UP:
        return `Moved up since ${label}`;
      case MOVEMENT.DOWN:
        return `Moved down since ${label}`;
      case MOVEMENT.NEW:
        return `New since ${label}`;
      case MOVEMENT.SAME:
        return `Same spot as ${label}`;
      default:
        return "";
    }
  }
  switch (movement.status) {
    case MOVEMENT.UP:
      return `Higher in ${label}`;
    case MOVEMENT.DOWN:
      return `Lower in ${label}`;
    case MOVEMENT.DROPPED:
      return `Not in ${label}`;
    case MOVEMENT.SAME:
      return `Same spot in ${label}`;
    default:
      return "";
  }
};

const SCRATCHED_PODIUM_VALUES = new Set(["SCRATCHED", "SCR"]);

/**
 * True when an archived race's podium says this runner was scratched.
 * Each podium array lines up with ITS OWN tip list (`podium` <-> `predictionData`
 * (Late Mail), `earlyPodium` <-> `earlyPredictionData` (Race Day tips)), so the runner
 * is found in that list first: a row number from another list (e.g. Early Mail) must
 * never be used to index it.
 */
export const isScratchedInPodium = (runner, raceResults) => {
  if (!runner || !raceResults) return false;
  const pairs = [
    [raceResults.predictionData?.firstFourData, raceResults.podium],
    [raceResults.earlyPredictionData?.firstFourData, raceResults.earlyPodium],
  ];
  return pairs.some(([tips, podium]) => {
    if (!Array.isArray(tips) || !Array.isArray(podium)) return false;
    const index = tips.findIndex(
      (tip) => tip && typeof tip === "object" && isSameRunner(runner, tip),
    );
    return index !== -1 && SCRATCHED_PODIUM_VALUES.has(podium[index]);
  });
};
